/**
 * Shared click-to-select behavior for the 2D and 3D marker renderers: selecting
 * an entity highlights it (via the selection store) and moves the camera onto
 * it. Satellites are framed together with their ground coverage footprint;
 * everything else gets a simple zoom-to-point.
 */

import { useEntitySelectionStore } from '@/stores/entity-selection-store';
import { useMapStore } from '@/stores/map-store';
import { CLIENT_CONFIG } from '@/config';
import { computeHorizonFootprint, groundRadiusKm } from '@/map/coverage/footprint-geometry';
import { SATELLITE_LAYER, type CoverageFrame } from '@/map/coverage/coverage-frame';
import type { MarkerData } from './marker-state';

/** Zoom level applied when a non-satellite entity is selected. */
const SELECT_ZOOM = 13;

/** Select an entity and move the camera to it (framing satellites with their footprint). */
export function selectEntity(data: MarkerData): void {
  useEntitySelectionStore.getState().select(data.uid);
  const frame = coverageFrameFor(data);
  if (frame) {
    useMapStore.getState().setFlyTo({ frame });
  } else {
    useMapStore.getState().setFlyTo({ lat: data.position.lat, lng: data.position.lng, zoom: SELECT_ZOOM });
  }
}

/**
 * Build a coverage frame for an elevated satellite, else undefined. The marker's
 * `altitude` is the exaggerated 3D shell altitude (m); the true altitude (for the
 * footprint radius) divides that back out by the display exaggeration.
 */
function coverageFrameFor(data: MarkerData): CoverageFrame | undefined {
  if (data.layer !== SATELLITE_LAYER || data.altitude === undefined || data.altitude <= 0) {
    return undefined;
  }
  const trueAltitudeKm = data.altitude / 1000 / CLIENT_CONFIG.satelliteShellExaggeration;
  return {
    lat: data.position.lat,
    lng: data.position.lng,
    ring: computeHorizonFootprint(data.position.lat, data.position.lng, trueAltitudeKm),
    groundRadiusMeters: groundRadiusKm(trueAltitudeKm) * 1000,
    shellAltMeters: data.altitude,
  };
}
